import React from 'react'
import { useTheme } from 'styled-components'
import type {
  DistributionData,
  DistributionStats,
  PriceMode,
  TranslationFunction,
} from './DistributionSection.types'
import { formatPrice } from './DistributionSection.utils'
import { HistogramChart, type HistogramChartRef } from './components'
import { ChartCard, GridContainer } from './DistributionSection.styles'

export type DistributionMetric = keyof DistributionData['stats']
export type DistributionChartRefs = Record<
  DistributionMetric,
  React.RefObject<HistogramChartRef | null>
>

interface DistributionChartsProps {
  data: DistributionData
  isRental: boolean
  priceMode?: PriceMode
  accentColor?: string
  separate: boolean
  chartRefs: DistributionChartRefs
  t: TranslationFunction
}

export const DistributionCharts = ({
  data,
  isRental,
  priceMode,
  accentColor,
  separate,
  chartRefs,
  t,
}: DistributionChartsProps) => {
  const theme = useTheme()
  const period = isRental
    ? t(
        priceMode === 'yearly'
          ? 'distributionView.perYear'
          : 'distributionView.perMonth'
      )
    : ''
  const charts: Array<{
    key: DistributionMetric
    title: string
    unit: string
    total?: boolean
  }> = [
    {
      key: 'rooms',
      title: 'rooms',
      unit: t('distribution.units.rooms'),
      total: true,
    },
    { key: 'price', title: isRental ? 'rent' : 'price', unit: `CHF${period}` },
    {
      key: 'priceM2',
      title: isRental ? 'rentM2' : 'priceM2',
      unit: `CHF/m²${period}`,
    },
    { key: 'area', title: 'area', unit: 'm²' },
    {
      key: 'constructionYear',
      title: 'constructionYear',
      unit: '',
      total: true,
    },
    {
      key: 'listingDuration',
      title: 'timeOnMarket',
      unit: t('distribution.units.days'),
    },
  ]

  return (
    <GridContainer>
      {charts.map(({ key, title, unit, total }) => {
        if (
          (key === 'constructionYear' || key === 'listingDuration') &&
          !data[key].length
        )
          return null
        const format = (value: number) =>
          key === 'price' || key === 'priceM2'
            ? formatPrice(value, isRental, priceMode)
            : value.toFixed(key === 'rooms' ? 1 : 0)
        const statsLabel = (stats: DistributionStats) => {
          if (!stats.total) return t('distribution.empty.title')
          const median =
            `${t('distribution.stats.median')}: ${format(stats.median)} ${unit}`.trim()
          const summary = total
            ? `${t('distribution.stats.total')}: ${stats.total.toLocaleString()}`
            : `${t('distribution.stats.avg')}: ${format(stats.avg)} ${unit}`.trim()
          return `${median} | ${summary}`
        }
        const series = separate
          ? data.categories?.map((item) => ({
              name: t(`common.propertyTypes.${item.category.toLowerCase()}`),
              data: item.data[key],
              color:
                item.category === 'Apartment'
                  ? theme.colors.primary
                  : theme.colors.success,
              statsLabel: statsLabel(item.data.stats[key]),
            }))
          : undefined

        return (
          <ChartCard key={key}>
            <HistogramChart
              ref={chartRefs[key]}
              title={t(`distribution.charts.${title}`)}
              data={data[key]}
              stats={data.stats[key]}
              unit={unit}
              accentColor={accentColor}
              height={250}
              statsLabel={statsLabel(data.stats[key])}
              series={series}
              listingsLabel={t('distributionView.listings')}
              emptyLabel={t('distribution.empty.title')}
            />
          </ChartCard>
        )
      })}
    </GridContainer>
  )
}
